import axios, { InternalAxiosRequestConfig } from 'axios';
import { isOldToken } from '@common/utils';
import Cookies from 'js-cookie';
import * as Sentry from '@sentry/react';
import { EStatusCode } from '@/enums';

const axiosInstanceAnalyze = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL_ANALYZE,
  headers: {
    'Content-Type': 'application/json',
  },
});

axiosInstanceAnalyze.interceptors.request.use(async (config) => {
  const token = Cookies.get('token');
  const isOldJwtToken = isOldToken(token!);
  if (isOldJwtToken) {
    Cookies.remove('token');
    Cookies.remove('main_organization');
    Cookies.remove('remember_me');
    Cookies.remove('fallback_workspace');
    window.location.href = '/login';
  }
  if (config.headers.Authorization) {
    return {
      ...config,
      headers: {
        ...config.headers,
        Authorization: `Bearer ${token}`,
      },
    } as InternalAxiosRequestConfig;
  }

  // eslint-disable-next-line no-param-reassign
  config.headers.Authorization = token ? `Bearer ${token}` : '';
  return config;
});

axiosInstanceAnalyze.interceptors.response.use(
  async (response) => response,
  (error) => {
    // Capture API errors with rich context
    Sentry.withScope((scope) => {
      // Add request context
      scope.setTag('request_url', error.config?.url || 'unknown');
      scope.setTag('request_method', error.config?.method || 'unknown');

      // Add response context
      const status = error?.response?.status;
      scope.setTag('response_status', status);

      // Add user/session context
      const organizationId = Cookies.get('main_organization');
      const workspaceId = Cookies.get('fallback_workspace');

      if (organizationId) {
        scope.setTag('organization_id', organizationId);
      }
      if (workspaceId) {
        scope.setTag('workspace_id', workspaceId);
      }

      // Add browser/device context
      scope.setContext('browser_info', {
        userAgent: navigator.userAgent,
        language: navigator.language,
        screenSize: `${window.screen.width}x${window.screen.height}`,
        viewportSize: `${window.innerWidth}x${window.innerHeight}`,
        connection: navigator.connection ? navigator.connection.effectiveType : 'unknown',
      });

      // Set appropriate error category
      scope.setTag('error_type', 'analyze_api_error');

      // Custom fingerprinting based on error type
      const url = error.config?.url || '';
      const endpoint = url.split('/').slice(-2).join('/');

      // Group API errors by endpoint and status
      scope.setFingerprint(['analyze-api-error', `endpoint-${endpoint}`, `status-${status}`]);

      // Capture the exception with enhanced context
      Sentry.captureException(error);
    });

    // Handle specific error cases
    if (error.response.status === EStatusCode.INVALID_CREDENTIALS) {
      Cookies.remove('token');
      Cookies.remove('main_organization');
      Cookies.remove('remember_me');
      Cookies.remove('fallback_workspace');
      window.location.href = '/login';
    }
    return Promise.reject(error.response);
  },
);

export default axiosInstanceAnalyze;
